import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api-client';
import { allocatedDownpaymentKey } from './use-allocated-downpayment';
import { blOrderInvoicesKey } from './use-bl-order-invoices';
import { invoicePaymentsKey } from './use-invoice-payments';
import type { InvoiceRow, PaymentRow } from '../types';

export interface RecordPaymentMutationArgs {
  readonly invoiceId: string;
  readonly orderId: string;
  readonly blOrderId?: string | undefined;
}

export interface RecordPaymentBody {
  readonly total_amount_paid: number;
  readonly paid_date: string;
  readonly bank_reference?: string | undefined;
  readonly reference_note?: string | undefined;
}

export interface RecordPaymentResult {
  readonly payment: PaymentRow;
  readonly invoice: InvoiceRow;
}

function invalidatePaymentQueries(
  qc: ReturnType<typeof useQueryClient>,
  args: RecordPaymentMutationArgs,
): void {
  void qc.invalidateQueries({ queryKey: invoicePaymentsKey(args.invoiceId) });
  void qc.invalidateQueries({ queryKey: ['order-advance-invoices', args.orderId] });
  void qc.invalidateQueries({ queryKey: ['order-invoices', args.orderId] });
  if (args.blOrderId !== undefined) {
    void qc.invalidateQueries({ queryKey: blOrderInvoicesKey(args.blOrderId) });
    void qc.invalidateQueries({ queryKey: allocatedDownpaymentKey(args.blOrderId) });
    void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
  }
}

export function useRecordPaymentMutation(args: RecordPaymentMutationArgs) {
  const qc = useQueryClient();
  return useMutation<RecordPaymentResult, Error, RecordPaymentBody>({
    mutationFn: (body) =>
      apiRequest<RecordPaymentResult>(`/invoices/${encodeURIComponent(args.invoiceId)}/payments`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      invalidatePaymentQueries(qc, args);
    },
  });
}
